// ──────────────────────────────────────────────────────
// Admin Auth Library — JWT token management
// Access token in memory, refresh via httpOnly cookie
// ──────────────────────────────────────────────────────

const API_BASE = '/api/backend';

let accessToken: string | null = null;
let tokenExpiresAt: number = 0;

/** Login with email + password → store access token in memory */
export async function adminLogin(email: string, password: string): Promise<{ permissions: string[] }> {
    const res = await fetch(`${API_BASE}/admin-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Login failed');
    }

    const data = await res.json();
    accessToken = data.accessToken;
    tokenExpiresAt = Date.now() + (data.expiresIn * 1000) - 30000; // 30s buffer

    // Fetch user info
    const me = await adminFetchJson<{ permissions: string[] }>('/admin-auth/me');
    return { permissions: me.permissions || [] };
}

/** Refresh access token via httpOnly cookie */
export async function adminRefresh(): Promise<boolean> {
    try {
        const res = await fetch(`${API_BASE}/admin-auth/refresh`, {
            method: 'POST',
            credentials: 'include',
        });

        if (!res.ok) return false;

        const data = await res.json();
        accessToken = data.accessToken;
        tokenExpiresAt = Date.now() + (data.expiresIn * 1000) - 30000;
        return true;
    } catch {
        return false;
    }
}

/** Logout — clear token + cookie */
export async function adminLogout(): Promise<void> {
    try {
        await fetch(`${API_BASE}/admin-auth/logout`, {
            method: 'POST',
            credentials: 'include',
        });
    } catch {
        // ignore
    }
    accessToken = null;
    tokenExpiresAt = 0;
}

/** Get current access token, refreshing if needed */
export async function getAccessToken(): Promise<string | null> {
    if (accessToken && Date.now() < tokenExpiresAt) {
        return accessToken;
    }

    // Try refresh
    const refreshed = await adminRefresh();
    return refreshed ? accessToken : null;
}

/** Check if user is authenticated (has valid token or can refresh) */
export async function isAuthenticated(): Promise<boolean> {
    const token = await getAccessToken();
    return !!token;
}

/** Authenticated fetch — adds Bearer header, auto-refreshes */
export async function adminFetch(path: string, options: RequestInit = {}): Promise<Response> {
    const token = await getAccessToken();

    const doFetch = (t: string | null) =>
        fetch(`${API_BASE}${path}`, {
            ...options,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(t ? { Authorization: `Bearer ${t}` } : {}),
                ...options.headers,
            },
        });

    let res = await doFetch(token);

    // If 401, try refresh once
    if (res.status === 401 && token) {
        const refreshed = await adminRefresh();
        if (refreshed) {
            res = await doFetch(accessToken);
        }
    }

    return res;
}

/** Convenience: fetch + parse JSON. On non-2xx, surfaces the first
 *  field-level reason (if the backend returned an `errors[]` array) alongside
 *  the top-level message so UI can show "product_type_v3: must be a valid
 *  product type" instead of an opaque "Product data violates QCare Standard v1".
 *  The full errors list is also attached to the thrown Error as `.errors` so
 *  callers that want to render the whole list can walk it. */
export async function adminFetchJson<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await adminFetch(path, options);
    if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
            message?: string;
            errors?: Array<{ code?: string; field?: string; reason?: string; message?: string }>;
        };
        const base = body.message || `Request failed: ${res.status}`;
        let detail = '';
        if (Array.isArray(body.errors) && body.errors.length > 0) {
            const first = body.errors[0];
            const field = first?.field || first?.code || '';
            const reason = first?.reason || first?.message || '';
            if (field || reason) {
                detail = ` — ${field ? field + ': ' : ''}${reason}`;
            }
            if (body.errors.length > 1) {
                detail += ` (+${body.errors.length - 1} more)`;
            }
        }
        const err = new Error(`${base}${detail}`) as Error & {
            errors?: typeof body.errors;
        };
        if (Array.isArray(body.errors)) err.errors = body.errors;
        throw err;
    }
    return res.json();
}
